// --- START OF FILE ProjectDetail/panels/AffairesPanel.js ---
import React, { useState, useCallback, useEffect, useRef, memo, useMemo } from 'react';
import { Plus, X, FileText, Loader2, Paperclip, Trash2, UploadCloud } from 'lucide-react';
import { useAuth } from '../../Auth'; // Ajustez le chemin si nécessaire

// --- AffaireFormFields Component ---
const AffaireFormFields = memo(({ /* ...props... */
    affaireData, isNew = false,
    stagedOfferFile, stagedPurchaseFile, // For new affaires
    selectedOfferFile, setSelectedOfferFile, // For staging replacement offer
    selectedPurchaseFile, setSelectedPurchaseFile, // For staging purchase file
    offerPreviewUrl, stagedPurchasePreviewUrl,
    offerLoading, purchaseLoading, isPurchaseLoading,
    handleNewOfferFileChange, handleNewPurchaseFileChange, // Handlers for NEW affaire files
    handleOfferFileChange, // Handler for EDITING offer file selection
    handlePurchaseFileChange, // Handler for EDITING purchase file selection
    handleRemoveOfferFile, handleRemovePurchaseFile, // Handlers for NEW affaire file REMOVAL
    handleRemoveSelectedOfferFile, // Handler for EDITING offer file CANCELLATION
    handleRemoveSelectedPurchaseFile, // Handler for EDITING purchase file CANCELLATION
    offerFileInputRef, purchaseFileInputRef,
    currentView, setCurrentView,
    handleAffaireFieldChange, handleAllocationToggle, handleAllocationChange,
    apiBaseUrl, iterations,
    handleCancel, handleNewAdd, handleAffaireSave,
    error,
    handleTriggerPurchaseExtract,
    sidebarWidth, // NOUVEAU: Récupérer la largeur de la sidebar
}) => {
    const formFieldsRef = useRef(null); // Référence pour le conteneur des champs du formulaire
    const [iframeHeight, setIframeHeight] = useState('400px'); // État pour la hauteur de l'iframe

    useEffect(() => {
        if (formFieldsRef.current) {
            // Calculer la hauteur disponible pour l'iframe
            // La hauteur totale du panneau est la hauteur de la fenêtre moins le padding et la hauteur du header/footer
            // On veut que l'iframe prenne au maximum 50% de la hauteur de la fenêtre
            const maxHeight = window.innerHeight * 0.5; 
            setIframeHeight(maxHeight);
        }
    }, [sidebarWidth]); // Recalculer si la largeur de la sidebar change

    const hasOfferToShow = isNew ? !!stagedOfferFile : (!!affaireData.file_path || !!selectedOfferFile);
    const hasPurchaseToShow = isNew ? !!stagedPurchaseFile : (!!affaireData.purchase_file_path || !!selectedPurchaseFile);
    let previewUrl = null;
    let previewTitle = '';
    let shouldShowPreviewArea = false;
    let currentFileLoading = false;

    if (currentView === 'offer') {
        currentFileLoading = offerLoading;
        if (isNew && stagedOfferFile && offerPreviewUrl) {
            previewUrl = offerPreviewUrl; previewTitle = 'Aperçu Offre (Nouveau)'; shouldShowPreviewArea = true;
        } else if (!isNew && selectedOfferFile) {
            try { previewUrl = URL.createObjectURL(selectedOfferFile); previewTitle = 'Aperçu Nouvelle Offre'; shouldShowPreviewArea = true; } catch (e) { previewUrl = null; }
        } else if (!isNew && affaireData.file_path) {
            previewUrl = `${apiBaseUrl}/affaires/files/${affaireData.file_path}`; previewTitle = `Offre ${affaireData.number || affaireData.id}`; shouldShowPreviewArea = true;
        }
    } else if (currentView === 'purchase') {
        currentFileLoading = isNew ? purchaseLoading : isPurchaseLoading;
        if (isNew && stagedPurchaseFile && stagedPurchasePreviewUrl) {
            previewUrl = stagedPurchasePreviewUrl; previewTitle = 'Aperçu Achat (Nouveau)'; shouldShowPreviewArea = true;
        } else if (!isNew && selectedPurchaseFile) {
             try { previewUrl = URL.createObjectURL(selectedPurchaseFile); previewTitle = 'Aperçu Nouvel Achat'; shouldShowPreviewArea = true; } catch (e) { previewUrl = null; }
        } else if (!isNew && affaireData.purchase_file_path) {
            previewUrl = `${apiBaseUrl}/affaires/files/${affaireData.purchase_file_path}`; previewTitle = `Achat ${affaireData.number || affaireData.id}`; shouldShowPreviewArea = true;
        }
    }

    const totalAllocated = useMemo(() => { 
        const allocations = Array.isArray(affaireData.iteration_allocations) ? affaireData.iteration_allocations : [];
        if (!allocations || allocations.length === 0) {
            return { percentage: 0, amount: 0 };
        }
        const totalPercentage = allocations.reduce((sum, alloc) => sum + (parseFloat(alloc.percentage) || 0), 0);
        const totalAmount = allocations.reduce((sum, alloc) => sum + ((parseFloat(affaireData.total) || 0) * (parseFloat(alloc.percentage) || 0) / 100), 0);
        return { percentage: totalPercentage, amount: totalAmount };
    }, [affaireData.iteration_allocations, affaireData.total]);

    const isTotalPercentageMismatch = Array.isArray(affaireData.iteration_allocations) && 
                                      affaireData.iteration_allocations.length > 0 && 
                                      Math.abs(totalAllocated.percentage - 100) > 0.1;


    const handlePercentageInputChange = useCallback((iterId, value) => { 
        const cleanedValue = value.replace(/[^0-9.,]/g, '').replace(',', '.');
        if (cleanedValue === '' || cleanedValue === '.' || /^\d*\.?\d*$/.test(cleanedValue)) {
            let numericValue = parseFloat(cleanedValue); if (isNaN(numericValue)) numericValue = 0;
            handleAllocationChange(iterId, 'percentage', Math.max(0, Math.min(100, numericValue)), cleanedValue);
        }
    }, [handleAllocationChange]);

    return (
        <div className="space-y-4" ref={formFieldsRef}>
             <div className="mb-4 p-3 border rounded bg-gray-50">
                {(hasOfferToShow || hasPurchaseToShow) && (hasOfferToShow !== hasPurchaseToShow || (hasOfferToShow && hasPurchaseToShow)) && (
                    <div className="flex justify-center space-x-2 mb-3 border-b pb-2">
                        {hasOfferToShow && (<button type="button" onClick={() => setCurrentView('offer')} className={`px-2 py-1 rounded text-xs font-medium flex items-center transition-colors ${currentView === 'offer' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}> <FileText className="h-3 w-3 mr-1" /> Offre {offerLoading && <Loader2 className="h-3 w-3 animate-spin ml-1"/>} </button> )}
                        {hasPurchaseToShow && ( <button type="button" onClick={() => setCurrentView('purchase')} className={`px-2 py-1 rounded text-xs font-medium flex items-center transition-colors ${currentView === 'purchase' ? 'bg-yellow-100 text-yellow-700' : 'text-gray-600 hover:bg-gray-100'}`}> <Paperclip className="h-3 w-3 mr-1" /> Achat {purchaseLoading && <Loader2 className="h-3 w-3 animate-spin ml-1"/>} {isPurchaseLoading && <Loader2 className="h-3 w-3 animate-spin ml-1"/>} </button> )}
                    </div>
                )}
                {shouldShowPreviewArea && ( <div style={{ height: iframeHeight }} className="flex items-center justify-center border rounded-md overflow-hidden bg-gray-100 mb-3"> {currentFileLoading ? (<div className="flex items-center text-gray-500"> <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement... </div>) : previewUrl ? (<iframe src={`${previewUrl}#toolbar=0&navpanes=0&view=FitH`} title={previewTitle} width="100%" height="100%" className="border-0"/>) : (<p className="text-xs text-gray-500 italic text-center p-4">Aperçu indisponible.</p>)} </div> )}
                 <div className="space-y-3">
                    {/* --- Offer Area --- */}
                    {isNew && !stagedOfferFile && !offerLoading && ( <div className="flex flex-col items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:border-blue-400" onClick={() => offerFileInputRef.current?.click()}> <UploadCloud className="h-5 w-5 text-gray-400 mb-1" /> <p className="text-xs text-gray-600 mb-1">Ajouter Document Offre (PDF/Image) <span className="text-gray-400">(Optionnel)</span></p> <input ref={offerFileInputRef} type="file" accept="application/pdf,image/*" onChange={handleNewOfferFileChange} className="hidden" /> <span className="text-xs text-blue-500">Parcourir</span> </div> )}
                    {isNew && stagedOfferFile && !offerLoading && ( <div className="flex items-center justify-between bg-blue-50 p-2 rounded border border-blue-200 text-xs"> <span className="truncate mr-2" title={stagedOfferFile.name}><FileText className="h-3 w-3 inline mr-1"/>{stagedOfferFile.name}</span> <button type="button" onClick={handleRemoveOfferFile} title="Retirer l'offre" className="p-1 rounded text-gray-500 hover:text-gray-700"><X className="h-3.5 w-3.5" /></button> </div> )}
                    {/* Pas d'analyse automatique */}
                    {!isNew && affaireData.file_path && !selectedOfferFile && ( <div className="flex items-center justify-between text-xs bg-blue-50 p-2 rounded border border-blue-200"> <div className="flex items-center space-x-2 truncate"> <FileText className="h-3 w-3 text-blue-600 flex-shrink-0"/> <span className="text-blue-800 truncate" title={affaireData.file_path}> Offre: {affaireData.file_path.substring(affaireData.file_path.indexOf('_') + 1)} </span> </div> <div className="flex space-x-1 flex-shrink-0"> <button type="button" onClick={() => offerFileInputRef.current?.click()} title="Remplacer le document d'offre" className="p-1 text-blue-600 hover:text-blue-800"> <UploadCloud className="h-4 w-4"/> </button> <input ref={offerFileInputRef} type="file" accept="application/pdf,image/*" onChange={handleOfferFileChange} className="hidden"/> </div> </div> )}
                    {!isNew && !affaireData.file_path && !selectedOfferFile && ( <div className="flex flex-col items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:border-blue-400" onClick={() => offerFileInputRef.current?.click()}> <UploadCloud className="h-5 w-5 text-gray-400 mb-1" /> <p className="text-xs text-gray-600 mb-1">Ajouter Document Offre <span className="text-gray-400">(Optionnel)</span></p> <input ref={offerFileInputRef} type="file" accept="application/pdf,image/*" onChange={handleOfferFileChange} className="hidden"/> <span className="text-xs text-blue-500">Parcourir</span> </div> )}
                    {!isNew && selectedOfferFile && ( <div className="mt-2"> <h4 className="text-xs font-medium text-gray-700 mb-1">Nouveau Document Offre</h4> <div className="flex items-center justify-between bg-blue-50 p-2 rounded border border-blue-200 text-xs"> <span className="truncate mr-2" title={selectedOfferFile.name}><FileText className="h-3 w-3 inline mr-1"/>Prêt: {selectedOfferFile.name}</span> <button type="button" onClick={handleRemoveSelectedOfferFile} title="Annuler le remplacement" className="p-1 rounded text-gray-500 hover:text-gray-700"> <X className="h-3.5 w-3.5" /> </button> </div> </div> )}

                    {(hasOfferToShow || !isNew) && (
                        <div className="border-t pt-3 mt-3">
                            <h4 className="text-xs font-medium text-gray-700 mb-1">Document Achat Associé <span className="text-gray-400">(Optionnel)</span></h4>
                            {isNew && !stagedPurchaseFile && !purchaseLoading && ( <div className="flex flex-col items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:border-green-400" onClick={() => purchaseFileInputRef.current?.click()}> <UploadCloud className="h-5 w-5 text-gray-400 mb-1" /> <p className="text-xs text-gray-600 mb-1">Ajouter Facture/Justificatif</p> <input ref={purchaseFileInputRef} type="file" accept="application/pdf,image/*" onChange={handleNewPurchaseFileChange} className="hidden"/> <span className="text-xs text-green-600">Parcourir</span> </div> )}
                            {isNew && stagedPurchaseFile && !purchaseLoading && ( <div className="flex items-center justify-between bg-yellow-50 p-2 rounded border border-yellow-200 text-xs"> <span className="truncate mr-2" title={stagedPurchaseFile.name}><Paperclip className="h-3 w-3 inline mr-1"/>Ajouté: {stagedPurchaseFile.name}</span> <button type="button" onClick={handleRemovePurchaseFile} title="Retirer le fichier d'achat" className="p-1 rounded text-gray-500 hover:text-gray-700"> <X className="h-3.5 w-3.5" /> </button> </div> )}
                            {/* Pas d'analyse automatique */}
                            {!isNew && (
                                <>
                                    {!affaireData.purchase_file_path && !selectedPurchaseFile && ( <div className="flex flex-col items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:border-green-400" onClick={() => purchaseFileInputRef.current?.click()}> <UploadCloud className="h-5 w-5 text-gray-400 mb-1" /> <p className="text-xs text-gray-600 mb-1">Ajouter Facture/Justificatif</p> <input ref={purchaseFileInputRef} type="file" accept="application/pdf,image/*" onChange={handlePurchaseFileChange} className="hidden"/> <span className="text-xs text-green-600">Parcourir</span> </div> )}
                                    {affaireData.purchase_file_path && !selectedPurchaseFile && ( <div className="flex items-center justify-between text-xs bg-green-50 p-2 rounded border border-green-200"> <div className="flex items-center space-x-2 truncate"> <Paperclip className="h-3 w-3 text-green-600 flex-shrink-0"/> <span className="text-green-800 truncate" title={affaireData.purchase_file_path}>Achat: {affaireData.purchase_file_path.split('_').pop()}</span> </div> <div className="flex space-x-1 flex-shrink-0"> <button type="button" onClick={() => purchaseFileInputRef.current?.click()} title="Remplacer le document d'achat" className="p-1 text-blue-600 hover:text-blue-800"><UploadCloud className="h-4 w-4"/></button> <input ref={purchaseFileInputRef} type="file" accept="application/pdf,image/*" onChange={handlePurchaseFileChange} className="hidden"/> </div> </div> )}
                    {selectedPurchaseFile && ( <div className="mt-2"> <h4 className="text-xs font-medium text-gray-700 mb-1">Nouveau Document Achat</h4> <div className="flex items-center justify-between bg-yellow-50 p-2 rounded border border-yellow-200 text-xs"> <span className="truncate mr-2" title={selectedPurchaseFile.name}><Paperclip className="h-3 w-3 inline mr-1"/>Prêt: {selectedPurchaseFile.name}</span> <div className="flex items-center space-x-1"> <button type="button" onClick={handleRemoveSelectedPurchaseFile} disabled={isPurchaseLoading} title="Annuler la sélection" className={`p-1 rounded text-gray-500 hover:text-gray-700 ${isPurchaseLoading ? 'opacity-50 cursor-not-allowed' : ''}`}> <X className="h-3.5 w-3.5" /> </button> </div> </div> </div> )}
                                </>
                            )}
                        </div>
                    )}
                 </div>
             </div>

            {!(isNew && offerLoading) && (
                <div className="mt-4 border-t pt-4">
                   <h4 className="text-xs font-medium text-gray-700 mb-2">Offre</h4>
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                     <div className="sm:col-span-2">
                       <label className="block text-xs font-medium text-gray-700">Nom / Référence de l'offre</label>
                       <input
                         type="text"
                         value={affaireData.number || ''}
                         onChange={e => handleAffaireFieldChange('number', e.target.value)}
                         className="border rounded px-2 py-1 w-full text-sm mt-1"
                         placeholder="ex: OFF-2025-001 / Offre Initiale"
                       />
                     </div>
                     <div>
                       <label className="block text-xs font-medium text-gray-700">Budget Achat HT (€)</label>
                       <input type="number" step="0.01" min="0" value={affaireData.purchase_total ?? ''} onChange={e => handleAffaireFieldChange('purchase_total', e.target.value)} className="border rounded px-2 py-1 w-full text-sm mt-1"/>
                     </div>
                     <div>
                       <label className="block text-xs font-medium text-gray-700">Budget Heures (h)</label>
                       <input type="number" step="0.25" min="0" value={affaireData.hours_planned ?? ''} onChange={e => handleAffaireFieldChange('hours_planned', e.target.value)} className="border rounded px-2 py-1 w-full text-sm mt-1"/>
                     </div>
                     <div>
                       <label className="block text-xs font-medium text-gray-700">Statut de l'offre</label>
                       <select
                         value={affaireData.status || 'proposition en cours'}
                         onChange={e => handleAffaireFieldChange('status', e.target.value)}
                         className="border rounded px-2 py-1 w-full text-sm mt-1"
                       >
                         <option value="proposition en cours">Proposition en cours</option>
                         <option value="gagnée">Gagnée</option>
                         <option value="perdue">Perdue</option>
                       </select>
                     </div>
                   </div>
                    <div className="flex justify-end space-x-2 pt-3 border-t mt-3">
                         <button type="button" onClick={handleCancel} className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-xs font-medium">Annuler</button>
                         <button type="button" onClick={isNew ? handleNewAdd : handleAffaireSave} disabled={ offerLoading || purchaseLoading || isPurchaseLoading } className={`px-3 py-1 rounded text-xs font-medium text-white transition-colors duration-150 ${ (offerLoading || purchaseLoading || isPurchaseLoading) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700' }`} > {(offerLoading || purchaseLoading || isPurchaseLoading) && <Loader2 className="h-4 w-4 animate-spin inline mr-1" />} {isNew ? 'Ajouter Offre' : 'Enregistrer Modifications'} </button>
                   </div>
                    {error && <p className="text-red-500 text-xs mt-2 text-right">{error}</p>}
                </div>
            )}
        </div>
    );
});

// --- Main AffairesPanel Component ---
const AffairesPanel = ({
    projectId,
    affaires,
    iterations,
    apiBaseUrl,
    onAddAffaire,
    onUpdateAffaire,
    onDeleteAffaire,
    onUpdateAffairePurchaseExtract,
    onAffaireFileExtract,
    sidebarWidth, // NOUVEAU: Récupérer la largeur de la sidebar
}) => {
    const [expandedAffaireId, setExpandedAffaireId] = useState(null);
    const [editingAffaire, setEditingAffaire] = useState(null);
    const [newAffaireData, setNewAffaireData] = useState({ status: 'proposition en cours', iteration_allocations: [], number: null, total: null, purchase_total: null, hours_planned: null });
    const [stagedOfferFile, setStagedOfferFile] = useState(null);
    const [stagedPurchaseFile, setStagedPurchaseFile] = useState(null);
    const [selectedOfferFile, setSelectedOfferFile] = useState(null);
    const [selectedPurchaseFile, setSelectedPurchaseFile] = useState(null);
    const [offerPreviewUrl, setOfferPreviewUrl] = useState(null); 
    const [stagedPurchasePreviewUrl, setStagedPurchasePreviewUrl] = useState(null); 
    const [offerLoading, setOfferLoading] = useState(false);
    const [purchaseLoading, setPurchaseLoading] = useState(false);
    const [isPurchaseLoading, setIsPurchaseLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentView, setCurrentView] = useState('offer');

    const offerFileInputRef = useRef(null);
    const purchaseFileInputRef = useRef(null);
    const { getAuthHeaders } = useAuth();

    const displayError = useCallback((message, duration = 5000) => { setError(message); setTimeout(() => setError(null), duration); }, []);

    const resetLocalState = useCallback(() => {
        setEditingAffaire(null);
        setNewAffaireData({ status: 'proposition en cours', iteration_allocations: [], number: null, total: null, purchase_total: null, hours_planned: null });
        setStagedOfferFile(null);
        setStagedPurchaseFile(null);
        setSelectedOfferFile(null);
        setSelectedPurchaseFile(null);
        if (offerPreviewUrl) { try { URL.revokeObjectURL(offerPreviewUrl); } catch(e){} }
        setOfferPreviewUrl(null);
        if (stagedPurchasePreviewUrl) { try { URL.revokeObjectURL(stagedPurchasePreviewUrl); } catch(e){} }
        setStagedPurchasePreviewUrl(null);
        setOfferLoading(false); setPurchaseLoading(false); setIsPurchaseLoading(false);
        setError(null); setCurrentView('offer');
        if (offerFileInputRef.current) offerFileInputRef.current.value = "";
        if (purchaseFileInputRef.current) purchaseFileInputRef.current.value = "";
    }, [offerPreviewUrl, stagedPurchasePreviewUrl]);

    const handleSelectAffaire = useCallback((aff) => { 
        const isOpening = expandedAffaireId !== aff.id;
        const isDifferent = expandedAffaireId !== null && expandedAffaireId !== 'new' && expandedAffaireId !== aff.id;
        if (isOpening || isDifferent) { resetLocalState(); }
        setExpandedAffaireId((prev) => (prev === aff.id ? null : aff.id));
        if (isOpening) {
            const enrichedAlloc = (Array.isArray(aff.iteration_allocations) ? aff.iteration_allocations : []).map(a => ({ ...a, percentage: parseFloat(a.percentage) || 0 }));
            setEditingAffaire({ ...aff, iteration_allocations: enrichedAlloc });
            setCurrentView(aff.file_path ? 'offer' : aff.purchase_file_path ? 'purchase' : 'offer');
        }
    }, [expandedAffaireId, resetLocalState]);

    const handleNewAffaireClick = useCallback(() => { 
        resetLocalState();
        setExpandedAffaireId('new');
        setCurrentView('offer');
    }, [resetLocalState]);

    const handleAffaireFieldChange = useCallback((field, value) => { 
        const isNew = expandedAffaireId === 'new';
        const stateSetter = isNew ? setNewAffaireData : setEditingAffaire;
        stateSetter(prev => {
            const newState = { ...prev, [field]: value };
            if (field === 'total' && Array.isArray(newState.iteration_allocations)) {
                const newTotal = parseFloat(value) || 0;
                newState.iteration_allocations = newState.iteration_allocations.map(alloc => ({ ...alloc, amount: newTotal > 0 ? newTotal * (parseFloat(alloc.percentage) || 0) / 100 : 0, }));
            }
            return newState;
        });
    }, [expandedAffaireId]);

    const handleAllocationToggle = useCallback((iterId, isSelected) => { 
        const isNew = expandedAffaireId === 'new';
        const stateSetter = isNew ? setNewAffaireData : setEditingAffaire;
        stateSetter(prev => {
            const currentAllocs = Array.isArray(prev.iteration_allocations) ? prev.iteration_allocations : [];
            let updatedAllocs;
            if (isSelected) { 
                updatedAllocs = currentAllocs.some(a => a.iteration_id === iterId) ? 
                                currentAllocs : 
                                [...currentAllocs, { iteration_id: iterId, amount: 0, percentage: 0, rawPercentageInput: '0' }];
            } else { 
                updatedAllocs = currentAllocs.filter(a => a.iteration_id !== iterId); 
            }
            const selectedCount = updatedAllocs.length;
            let finalAllocs = updatedAllocs;
            if (selectedCount === 1 && updatedAllocs[0].percentage === 0) { 
                 const totalAffaireValue = parseFloat(prev.total) || 0;
                 finalAllocs = [{...updatedAllocs[0], percentage: 100, amount: totalAffaireValue, rawPercentageInput: '100'}];
            }
            return { ...prev, iteration_allocations: finalAllocs };
        });
     }, [expandedAffaireId]);

    const handleAllocationChange = useCallback((iterId, field, numericValue, rawValue = null) => { 
        if (field !== 'percentage') return;
        const isNew = expandedAffaireId === 'new';
        const stateSetter = isNew ? setNewAffaireData : setEditingAffaire;
        stateSetter(prev => {
            const total = parseFloat(prev.total) || 0;
            const currentAllocs = Array.isArray(prev.iteration_allocations) ? prev.iteration_allocations : [];
            const updatedAllocs = currentAllocs.map(alloc => { if (alloc.iteration_id === iterId) { const newAmount = total > 0 ? (numericValue / 100) * total : 0; return { ...alloc, percentage: numericValue, amount: newAmount, rawPercentageInput: rawValue }; } return alloc; });
            return { ...prev, iteration_allocations: updatedAllocs };
        });
     }, [expandedAffaireId]);

    const handleNewOfferFileChange = useCallback(async (e) => {
        const file = e.target.files && e.target.files[0];
        setStagedOfferFile(file || null);
        setOfferPreviewUrl(prevUrl => {
            if (prevUrl) { try { URL.revokeObjectURL(prevUrl); } catch(e){} }
            return file ? URL.createObjectURL(file) : null;
        });
        // Plus d'extraction automatique ni de modification des champs
        if (offerFileInputRef.current) offerFileInputRef.current.value = "";
    }, []); 

    const handleNewPurchaseFileChange = useCallback(async (e) => {
         const file = e.target.files && e.target.files[0];
         setStagedPurchaseFile(file || null);
         setStagedPurchasePreviewUrl(prevUrl => {
            if (prevUrl) { try { URL.revokeObjectURL(prevUrl); } catch(e){} }
            return file ? URL.createObjectURL(file) : null;
         });
         // Plus d'extraction automatique
         if (purchaseFileInputRef.current) purchaseFileInputRef.current.value = "";
    }, []); 

    const handleRemoveOfferFile = useCallback(() => {
        setStagedOfferFile(null);
        setOfferPreviewUrl(prevUrl => { 
             if (prevUrl) { try { URL.revokeObjectURL(prevUrl); } catch(e){} }
             return null;
        });
        if (offerFileInputRef.current) offerFileInputRef.current.value = "";
    }, []);

    const handleRemovePurchaseFile = useCallback(() => {
        setStagedPurchaseFile(null);
        setStagedPurchasePreviewUrl(prevUrl => { 
             if (prevUrl) { try { URL.revokeObjectURL(prevUrl); } catch(e){} }
             return null;
        });
        if (purchaseFileInputRef.current) purchaseFileInputRef.current.value = "";
    }, []);

    const handleOfferFileChange = useCallback((e) => { 
        setSelectedOfferFile(e.target.files?.[0] || null);
        setError(null);
        if (e.target) e.target.value = "";
    }, []);

    const handlePurchaseFileChange = useCallback((e) => { 
        const file = e.target.files?.[0] || null;
        setSelectedPurchaseFile(file);
        setError(null);
        if (e.target) e.target.value = "";
    }, []);

    const handleRemoveSelectedOfferFile = useCallback(() => { 
        setSelectedOfferFile(null);
        if (offerFileInputRef.current) offerFileInputRef.current.value = "";
    }, []);

    const handleRemoveSelectedPurchaseFile = useCallback(() => { 
        setSelectedPurchaseFile(null);
        if (purchaseFileInputRef.current) purchaseFileInputRef.current.value = "";
    }, []);

    const handleNewAdd = useCallback(async () => {
         // Création sans extraction et sans exigence de total
         if (expandedAffaireId !== 'new') return;
         setOfferLoading(true); setPurchaseLoading(true);
         try {
             setError(null);
             await onAddAffaire(newAffaireData, stagedOfferFile, stagedPurchaseFile);
             resetLocalState(); 
             setExpandedAffaireId(null);
         } catch (addErr) { console.error("Erreur création offre:", addErr); }
         finally { setOfferLoading(false); setPurchaseLoading(false); }
     }, [expandedAffaireId, newAffaireData, stagedOfferFile, stagedPurchaseFile, onAddAffaire, displayError, resetLocalState]); 

    const handleAffaireSave = useCallback(async () => {
        if (!editingAffaire || !editingAffaire.id) return;
        try {
            setError(null);
            await onUpdateAffaire(editingAffaire.id, editingAffaire, selectedOfferFile);
            resetLocalState(); 
            setExpandedAffaireId(null);
        } catch (updateErr) { console.error("Erreur MAJ offre:", updateErr); }
    }, [editingAffaire, onUpdateAffaire, selectedOfferFile, displayError, resetLocalState]); 

    const handleCancel = useCallback(() => {
        resetLocalState(); 
        setExpandedAffaireId(null);
    }, [resetLocalState]);


    const handleTriggerPurchaseExtract = useCallback(async (affaireIdToProcess) => { 
        if (!selectedPurchaseFile || !affaireIdToProcess || affaireIdToProcess === 'new') {
            if (!affaireIdToProcess || affaireIdToProcess === 'new') {
                 displayError("Impossible d'analyser : aucune offre valide sélectionnée.");
            } else if (!selectedPurchaseFile) {
                 displayError("Veuillez sélectionner un fichier d'achat.");
            }
            return;
        }
        setIsPurchaseLoading(true); setError(null);
        try {
            const updatedAffaireDataOnly = await onUpdateAffairePurchaseExtract(affaireIdToProcess, selectedPurchaseFile);
            setEditingAffaire(prev => {
                if (prev && prev.id === affaireIdToProcess) {
                    return {
                      ...prev, 
                      purchase_total: updatedAffaireDataOnly.purchase_total, 
                      purchase_file_path: updatedAffaireDataOnly.purchase_file_path, 
                    };
                 }
                 return prev; 
            });
            setSelectedPurchaseFile(null); 
            if (purchaseFileInputRef.current) purchaseFileInputRef.current.value = "";
            setCurrentView('purchase');
        } catch (err) {
            setSelectedPurchaseFile(null); 
            if (purchaseFileInputRef.current) purchaseFileInputRef.current.value = "";
            displayError(`Erreur analyse/liaison achat: ${err.message || 'Erreur inconnue'}`);
        } finally {
            setIsPurchaseLoading(false);
        }
    }, [selectedPurchaseFile, onUpdateAffairePurchaseExtract, displayError, setEditingAffaire, setCurrentView]); 

    useEffect(() => {
        const offerUrl = offerPreviewUrl;
        const purchaseUrl = stagedPurchasePreviewUrl;
        return () => { if (offerUrl) URL.revokeObjectURL(offerUrl); if (purchaseUrl) URL.revokeObjectURL(purchaseUrl); };
    }, [offerPreviewUrl, stagedPurchasePreviewUrl]);

    const statusDisplayMap = {
        "proposition en cours": "Proposition en cours",
        "gagnée": "Gagnée",
        "perdue": "Perdue",
    };
    const statusColorMap = {
        "proposition en cours": "bg-yellow-100 text-yellow-800",
        "gagnée": "bg-green-100 text-green-800",
        "perdue": "bg-red-100 text-red-800",
    };

    return (
        <div className="space-y-2">
            <h3 className="text-lg font-semibold mb-2">Offres</h3>
            {affaires.length > 0 ? (
                affaires.map((aff) => (
                    <div key={aff.id} className="border rounded-lg overflow-hidden bg-white">
                        <div className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded" onClick={() => handleSelectAffaire(aff)}>
                             <div className="flex items-center space-x-2 min-w-0"> 
                                {aff.file_path && <FileText className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" title="Document d'offre lié"/>}
                                {aff.purchase_file_path && (<Paperclip className="h-3.5 w-3.5 text-green-500 flex-shrink-0" title="Document d'achat lié" />)} 
                                <span className="font-medium text-sm truncate" title={aff.number || `Offre #${aff.id}`}>{aff.number || `Offre #${aff.id}`}</span>
                             </div>
                            <span className={`text-xs capitalize px-1.5 py-0.5 rounded-full whitespace-nowrap ${statusColorMap[aff.status] || 'bg-gray-100 text-gray-800'}`}>
                                {statusDisplayMap[aff.status] || aff.status || 'N/A'}
                            </span>
                        </div>
                        {expandedAffaireId === aff.id && editingAffaire && (
                            <div className="mt-1 p-3 border-t border-gray-200">
                                <AffaireFormFields
                                    offerPreviewUrl={null}
                                    stagedPurchasePreviewUrl={null}
                                    affaireData={editingAffaire} isNew={false}
                                    selectedOfferFile={selectedOfferFile} setSelectedOfferFile={setSelectedOfferFile}
                                    selectedPurchaseFile={selectedPurchaseFile} setSelectedPurchaseFile={setSelectedPurchaseFile}
                                    handleOfferFileChange={handleOfferFileChange} handlePurchaseFileChange={handlePurchaseFileChange}
                                    handleRemoveSelectedOfferFile={handleRemoveSelectedOfferFile} handleRemoveSelectedPurchaseFile={handleRemoveSelectedPurchaseFile}
                                    isPurchaseLoading={isPurchaseLoading}
                                    stagedOfferFile={null} stagedPurchaseFile={null}
                                    offerLoading={false} purchaseLoading={false}
                                    handleNewOfferFileChange={()=>{}} handleNewPurchaseFileChange={()=>{}}
                                    handleRemoveOfferFile={()=>{}} handleRemovePurchaseFile={()=>{}}
                                    offerFileInputRef={offerFileInputRef} purchaseFileInputRef={purchaseFileInputRef}
                                    currentView={currentView} setCurrentView={setCurrentView}
                                    handleAffaireFieldChange={handleAffaireFieldChange} handleAllocationToggle={handleAllocationToggle}
                                    handleAllocationChange={handleAllocationChange}
                                    apiBaseUrl={apiBaseUrl} iterations={iterations}
                                    handleCancel={handleCancel} handleAffaireSave={handleAffaireSave}
                                    handleNewAdd={()=>{}} error={error}
                                    handleTriggerPurchaseExtract={handleTriggerPurchaseExtract}
                                    sidebarWidth={sidebarWidth} // Passer la largeur de la sidebar
                                />
                                <div className="mt-3 pt-3 border-t flex justify-end">
                                    <button type="button" onClick={() => onDeleteAffaire(aff.id)} className="flex items-center text-red-600 hover:text-red-800 text-xs font-medium p-1 rounded hover:bg-red-50"> <Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer Offre </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))
            ) : ( <p className="text-xs text-gray-500 italic text-center py-2">Aucune offre pour ce projet.</p> )}
            <div className="mt-2">
                 {expandedAffaireId === 'new' ? (
                     <div className="bg-white shadow-md rounded-lg p-3 border border-blue-200">
                          <div className="flex justify-between items-center mb-3"> <h3 className="text-sm font-semibold text-gray-800">Créer une nouvelle offre</h3> <button type="button" onClick={handleCancel} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"> <X className="h-5 w-5" /> </button> </div>
                          <AffaireFormFields
                               offerPreviewUrl={offerPreviewUrl}
                               stagedPurchasePreviewUrl={stagedPurchasePreviewUrl}
                               affaireData={newAffaireData} isNew={true}
                               stagedOfferFile={stagedOfferFile} stagedPurchaseFile={stagedPurchaseFile}
                               offerLoading={offerLoading} purchaseLoading={purchaseLoading}
                               handleNewOfferFileChange={handleNewOfferFileChange} handleNewPurchaseFileChange={handleNewPurchaseFileChange}
                               handleRemoveOfferFile={handleRemoveOfferFile} handleRemovePurchaseFile={handleRemovePurchaseFile}
                               selectedOfferFile={null} setSelectedOfferFile={()=>{}}
                               selectedPurchaseFile={null} setSelectedPurchaseFile={()=>{}}
                               handleOfferFileChange={()=>{}} handlePurchaseFileChange={()=>{}}
                               handleRemoveSelectedOfferFile={()=>{}} handleRemoveSelectedPurchaseFile={()=>{}}
                               isPurchaseLoading={false}
                               offerFileInputRef={offerFileInputRef} purchaseFileInputRef={purchaseFileInputRef}
                               currentView={currentView} setCurrentView={setCurrentView}
                               handleAffaireFieldChange={handleAffaireFieldChange} handleAllocationToggle={handleAllocationToggle}
                               handleAllocationChange={handleAllocationChange}
                               apiBaseUrl={apiBaseUrl} iterations={iterations}
                               handleCancel={handleCancel} handleNewAdd={handleNewAdd}
                               handleAffaireSave={()=>{}} error={error}
                               handleTriggerPurchaseExtract={()=>{}}
                               sidebarWidth={sidebarWidth} // Passer la largeur de la sidebar
                         />
                     </div>
                 ) : ( <button type="button" onClick={handleNewAffaireClick} className="w-full border-2 border-dashed border-gray-300 rounded-lg p-2 text-sm text-center text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center"> <Plus className="h-4 w-4 mr-1" /> Nouvelle Offre </button> )}
            </div>
        </div>
    );
};

export default AffairesPanel;
// --- END OF FILE ProjectDetail/panels/AffairesPanel.js ---
